import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import PageHeader from '../../components/PageHeader';
import Board from './Board';
import ModelControls from './ModelControls';
import MoveLog from './MoveLog';
import { isDraw, legalMoves, newGame, play } from './game';
import { HUMAN, TWO_PLAYERS, VS_AI } from './opponents';

const DEFAULT_SETTINGS = { hintLegalMoves: true, thinkingLevel: 'low', fallback: true };

const COLUMN_SX = { width: '100%', maxWidth: 400, mx: 'auto' };

const isHotSeat = (players) => players.X === HUMAN && players.O === HUMAN;

const FAILURE_TEXT = {
    quota: 'AI quota used up for today',
    unavailable: 'Model not available',
};

const failureText = ({ code, cell }) => (code === 'illegal'
    ? `AI picked cell ${cell}, which is taken`
    : FAILURE_TEXT[code] ?? FAILURE_TEXT.unavailable);

const statusText = (game, players, hotSeat, failure) => {
    if (failure) return failureText(failure);
    if (game.winner) {
        if (hotSeat) return `${game.winner.mark} wins`;
        return players[game.winner.mark] === HUMAN ? 'You win' : 'AI wins';
    }
    if (isDraw(game)) return 'Draw';
    if (hotSeat) return `${game.turn} to play`;
    return players[game.turn] === HUMAN ? 'Your turn' : 'AI thinking…';
};

const TicTacToe = () => {
    const [game, setGame] = useState(newGame);
    const [players, setPlayers] = useState(TWO_PLAYERS);
    const [moves, setMoves] = useState([]);
    const [failure, setFailure] = useState(null);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);

    const hotSeat = isHotSeat(players);
    const currentPlayer = players[game.turn];
    const aiToMove = currentPlayer !== HUMAN && !game.over;
    const thinking = aiToMove && !failure;

    useEffect(() => {
        if (!thinking) return undefined;

        let cancelled = false;

        currentPlayer(game, settings)
            .then((answer) => {
                if (cancelled) return;
                setMoves((past) => [...past, { mark: game.turn, ...answer }]);
                if (answer.illegal) {
                    console.warn('Tic-tac-toe illegal move', { board: game.board, ...answer });
                    setFailure({ code: 'illegal', cell: answer.cell });
                    return;
                }
                console.info('Tic-tac-toe move', answer);
                setGame((current) => play(current, answer.cell));
            })
            .catch((error) => {
                if (cancelled) return;
                console.warn('Tic-tac-toe move failed', error.response?.data ?? error.message);
                setFailure({ code: error.response?.data?.code ?? 'unavailable' });
            });

        return () => { cancelled = true; };
    }, [thinking, currentPlayer, game, settings]);

    const reset = () => {
        setGame(newGame());
        setMoves([]);
        setFailure(null);
    };

    return (
        <Container maxWidth="md" sx={{ pt: 8, pb: 12 }}>
            <PageHeader
                eyebrow="TIC TAC TOE"
                title={<>Three<br />in a row.</>}
                intro="Play someone next to you, or take on an LLM opponent that moves over the network."
                sx={{ mb: 5 }}
            />

            <Stack spacing={4} alignItems="center">
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={players}
                    onChange={(event, next) => {
                        if (next === null) return;
                        setPlayers(next);
                        reset();
                    }}
                    disabled={thinking}
                    aria-label="Opponent"
                >
                    <ToggleButton value={TWO_PLAYERS}>Two players</ToggleButton>
                    <ToggleButton value={VS_AI}>vs AI</ToggleButton>
                </ToggleButtonGroup>

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={4}
                    justifyContent="center"
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    sx={{ width: '100%' }}
                >
                    <Box sx={COLUMN_SX}>
                        <Typography variant="h5" align="center" sx={{ mb: 2, fontWeight: 700 }} aria-live="polite">
                            {statusText(game, players, hotSeat, failure)}
                        </Typography>

                        {failure && (
                            <Stack alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                {failure.code === 'illegal' && !settings.hintLegalMoves && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Spelling out the legal cells usually avoids this.
                                    </Typography>
                                )}
                                <Button variant="outlined" size="small" onClick={() => setFailure(null)}>
                                    Retry
                                </Button>
                            </Stack>
                        )}

                        <Board
                            cells={game.board}
                            playable={aiToMove ? [] : legalMoves(game)}
                            winningLine={game.winner?.line}
                            onCellClick={(cell) => setGame((current) => play(current, cell))}
                        />
                    </Box>

                    {!hotSeat && (
                        <Box sx={COLUMN_SX}>
                            <ModelControls settings={settings} onChange={setSettings} disabled={thinking} />
                            <MoveLog moves={moves} />
                        </Box>
                    )}
                </Stack>

                <Button variant="contained" color="primary" disableElevation onClick={reset} disabled={thinking}>
                    {game.over ? 'Play again' : 'Reset board'}
                </Button>
            </Stack>
        </Container>
    );
};

export default TicTacToe;
